/*
 * Reduce en el navegador los PDF que superan un peso máximo (fórmulas escaneadas):
 * renderiza cada página con pdf.js, la re-codifica en JPEG y rearma el PDF con jsPDF.
 * Usa la misma escalera calidad/resolución que ImageCompression, aplicada a todas
 * las páginas a la vez porque el límite es para el archivo completo.
 * Las librerías (~1.8MB) viven en assets y solo se descargan la primera vez que se usan.
 */
declare const pdfjsLib: any;

export class PdfCompression {

  static readonly TYPE = 'application/pdf';

  private static readonly MAX_SIDES = [2000, 1600, 1280, 1024];
  private static readonly QUALITIES = [0.85, 0.75, 0.65, 0.55];
  // Tope por memoria en celular; si cabe o no en el límite lo decide la escalera de peso.
  static readonly MAX_PAGES = 20;
  private static readonly LIBS_PATH = 'assets/libs/pdf/';

  private static libsLoaded: Promise<void>;
  // Un solo worker para todos los PDF: evita descargar y arrancar pdf.worker en cada archivo.
  private static worker: any;

  static isCompressible(file: File): boolean {
    return file.type === PdfCompression.TYPE;
  }

  /** Devuelve un PDF de máximo `maxBytes` con las páginas como imagen; rechaza si no lo logra. */
  static async compress(file: File, maxBytes: number): Promise<File> {
    await PdfCompression.loadLibs();
    const { pages, renderSide } = await PdfCompression.renderPages(file);
    try {
      for (const maxSide of PdfCompression.MAX_SIDES.filter(side => side <= renderSide)) {
        for (const quality of PdfCompression.QUALITIES) {
          const jpegs = await PdfCompression.toJpegs(pages, maxSide, quality, maxBytes);
          if (!jpegs) {
            continue;
          }
          const blob = PdfCompression.buildPdf(pages, jpegs);
          if (blob.size <= maxBytes) {
            return new File([blob], file.name, { type: PdfCompression.TYPE, lastModified: Date.now() });
          }
        }
      }
      throw new Error('No fue posible reducir el PDF al tamaño máximo');
    } finally {
      // Libera la memoria de los canvas sin esperar al recolector (importante en celular).
      pages.forEach(page => page.canvas.width = page.canvas.height = 0);
    }
  }

  /** Mensaje para el usuario cuando el rechazo tiene una causa concreta (p. ej. demasiadas páginas). */
  static userMessage(error: any): string {
    return error && error.userMessage;
  }

  // Se renderiza una sola vez y los intentos siguientes reescalan desde ahí.
  private static async renderPages(file: File): Promise<{ pages: RenderedPage[], renderSide: number }> {
    const data = await PdfCompression.readFile(file);
    const doc = await pdfjsLib.getDocument({ data, worker: PdfCompression.worker }).promise;
    try {
      if (doc.numPages > PdfCompression.MAX_PAGES) {
        throw Object.assign(new Error('Demasiadas páginas'), {
          userMessage: `El PDF tiene ${doc.numPages} páginas y el máximo es ${PdfCompression.MAX_PAGES}. Adjunta solo las páginas de la fórmula.`
        });
      }
      const renderSide = PdfCompression.renderSide(doc.numPages);
      const pages: RenderedPage[] = [];
      for (let number = 1; number <= doc.numPages; number++) {
        const page = await doc.getPage(number);
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: renderSide / Math.max(base.width, base.height) });
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(viewport.width);
        canvas.height = Math.round(viewport.height);
        const context = canvas.getContext('2d');
        // Fondo blanco: las páginas sin fondo quedarían negras en JPEG.
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({ canvasContext: context, viewport }).promise;
        pages.push({ canvas, widthPt: base.width, heightPt: base.height });
        page.cleanup();
      }
      return { pages, renderSide };
    } finally {
      doc.destroy();
    }
  }

  // Con varias páginas el límite reparte pocos KB por página y las resoluciones altas nunca caben
  // (medido: 3 páginas caben desde 1600px, 5 o más desde 1280px); empezar abajo ahorra memoria y tiempo.
  private static renderSide(numPages: number): number {
    const sides = PdfCompression.MAX_SIDES;
    return numPages <= 2 ? sides[0] : numPages <= 4 ? sides[1] : sides[2];
  }

  /** JPEG de cada página, o null si la suma ya supera `maxBytes`. */
  private static async toJpegs(pages: RenderedPage[], maxSide: number, quality: number, maxBytes: number): Promise<Uint8Array[]> {
    const canvas = document.createElement('canvas');
    const jpegs: Uint8Array[] = [];
    let total = 0;
    for (const page of pages) {
      const scale = Math.min(1, maxSide / Math.max(page.canvas.width, page.canvas.height));
      canvas.width = Math.round(page.canvas.width * scale);
      canvas.height = Math.round(page.canvas.height * scale);
      canvas.getContext('2d').drawImage(page.canvas, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob>(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
      if (!blob) {
        return null;
      }
      total += blob.size;
      if (total > maxBytes) {
        return null;
      }
      jpegs.push(new Uint8Array(await PdfCompression.readFile(blob)));
    }
    canvas.width = canvas.height = 0;
    return jpegs;
  }

  // Cada página conserva su tamaño físico original (en puntos), así se imprime igual.
  private static buildPdf(pages: RenderedPage[], jpegs: Uint8Array[]): Blob {
    const { jsPDF } = (window as any).jspdf;
    const orientation = (page: RenderedPage) => page.widthPt > page.heightPt ? 'l' : 'p';
    const first = pages[0];
    const pdf = new jsPDF({ unit: 'pt', format: [first.widthPt, first.heightPt], orientation: orientation(first), compress: true });
    pages.forEach((page, index) => {
      if (index > 0) {
        pdf.addPage([page.widthPt, page.heightPt], orientation(page));
      }
      pdf.addImage(jpegs[index], 'JPEG', 0, 0, page.widthPt, page.heightPt, undefined, 'NONE');
    });
    return pdf.output('blob');
  }

  private static loadLibs(): Promise<void> {
    if (!PdfCompression.libsLoaded) {
      PdfCompression.libsLoaded = Promise.all([
        PdfCompression.loadScript('pdf.min.js'),
        PdfCompression.loadScript('jspdf.umd.min.js')
      ]).then(() => {
        pdfjsLib.GlobalWorkerOptions.workerSrc = PdfCompression.libUrl('pdf.worker.min.js');
        PdfCompression.worker = new pdfjsLib.PDFWorker();
      }).catch(error => {
        // Permite reintentar si falló la descarga (p. ej. sin conexión).
        PdfCompression.libsLoaded = null;
        throw error;
      });
    }
    return PdfCompression.libsLoaded;
  }

  private static loadScript(name: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = PdfCompression.libUrl(name);
      script.onload = () => resolve();
      script.onerror = () => {
        script.remove();
        reject(new Error(`No fue posible cargar ${name}`));
      };
      document.body.appendChild(script);
    });
  }

  private static libUrl(name: string): string {
    return new URL(PdfCompression.LIBS_PATH + name, document.baseURI).href;
  }

  // FileReader en lugar de Blob.arrayBuffer() para soportar Safari < 14.
  private static readFile(blob: Blob): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(blob);
    });
  }
}

interface RenderedPage {
  canvas: HTMLCanvasElement;
  widthPt: number;
  heightPt: number;
}
